import type { IHost, IPartner } from '../../redux/discovery/types';

const HOST_KEY = 'thspian.recent-contest-hosts';
const PARTNER_KEY = 'thspian.recent-contest-partners';
const MAX_RECENT = 8;

export type RecentHostPartnerEntry = {
    _id: string;
    name: string;
    description?: string;
    profile_picture?: string;
    selectedAt: number;
};

function readStorage(key: string): RecentHostPartnerEntry[] {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as RecentHostPartnerEntry[];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeStorage(key: string, entries: RecentHostPartnerEntry[]) {
    try {
        localStorage.setItem(key, JSON.stringify(entries.slice(0, MAX_RECENT)));
    } catch {
        /* quota / private mode */
    }
}

function toRecentEntry(entity: IHost | IPartner): RecentHostPartnerEntry {
    return {
        _id: entity._id,
        name: entity.name,
        description: entity.description,
        profile_picture: entity.profile_picture,
        selectedAt: Date.now(),
    };
}

function pushRecent(key: string, entity: IHost | IPartner) {
    const entry = toRecentEntry(entity);
    const next = [entry, ...readStorage(key).filter(item => item._id !== entry._id)];
    writeStorage(key, next);
}

export function getRecentHosts(): RecentHostPartnerEntry[] {
    return readStorage(HOST_KEY);
}

export function getRecentPartners(): RecentHostPartnerEntry[] {
    return readStorage(PARTNER_KEY);
}

export function pushRecentHost(host: IHost) {
    pushRecent(HOST_KEY, host);
}

export function pushRecentPartner(partner: IPartner) {
    pushRecent(PARTNER_KEY, partner);
}

export function recentEntryToHost(entry: RecentHostPartnerEntry): IHost {
    return {
        _id: entry._id,
        id: entry._id,
        name: entry.name,
        description: entry.description ?? '',
        profile_picture: entry.profile_picture ?? '',
        approved: true,
        created_at: '',
        created_by: '',
        createdAt: '',
        updatedAt: '',
        __v: 0,
    };
}

export function formatRecentInteractionLabel(selectedAt: number): string {
    const diffMs = Date.now() - selectedAt;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(selectedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
